import { useState, useEffect } from 'react';
import { PageHeader, Badge, cx, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface SecurityEvent {
  id: string;
  timestamp: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  source: string;
  title: string;
  description: string;
  status: 'open' | 'investigating' | 'resolved' | 'false_positive';
  assignee?: string;
  tags: string[];
}

const mockEvents: SecurityEvent[] = [
  {
    id: 'evt-001',
    timestamp: '2026-05-22T10:30:00Z',
    type: 'vulnerability',
    severity: 'critical',
    source: 'GitHub Dependabot',
    title: 'Critical SQL Injection vulnerability in express-validator',
    description: 'CVE-2024-12345: Remote code execution possible through malformed input validation',
    status: 'open',
    assignee: 'security-team',
    tags: ['sql-injection', 'rce', 'express', 'dependency']
  },
  {
    id: 'evt-002',
    timestamp: '2026-05-22T09:15:00Z',
    type: 'access_anomaly',
    severity: 'high',
    source: 'Azure AD',
    title: 'Suspicious login from unknown location',
    description: 'User john.doe@company.com logged in from IP 185.220.101.182 (TOR exit node)',
    status: 'investigating',
    assignee: 'incident-response',
    tags: ['tor', 'anomalous-login', 'azure-ad']
  },
  {
    id: 'evt-003',
    timestamp: '2026-05-22T08:45:00Z',
    type: 'malware',
    severity: 'high',
    source: 'CrowdStrike',
    title: 'Potentially unwanted program detected',
    description: 'Detected: Win.Adware.CoinMiner on endpoint LAPTOP-DEV-001',
    status: 'resolved',
    assignee: 'endpoint-team',
    tags: ['malware', 'coinminer', 'endpoint']
  },
  {
    id: 'evt-004',
    timestamp: '2026-05-22T07:20:00Z',
    type: 'policy_violation',
    severity: 'medium',
    source: 'AWS Config',
    title: 'S3 bucket public read access detected',
    description: 'Bucket "company-logs-backup" has public read permissions enabled',
    status: 'open',
    tags: ['s3', 'public-access', 'aws', 'data-exposure']
  },
  {
    id: 'evt-005',
    timestamp: '2026-05-22T06:00:00Z',
    type: 'network_intrusion',
    severity: 'medium',
    source: 'Suricata IDS',
    title: 'Port scanning activity detected',
    description: 'Multiple TCP SYN packets to closed ports from 203.0.113.42',
    status: 'false_positive',
    tags: ['port-scan', 'network', 'ids']
  }
];

const severityTone: Record<string, ToneType> = {
  critical: 'crit',
  high: 'warn',
  medium: 'info',
  low: 'muted',
  info: 'muted'
};

const statusTone: Record<string, ToneType> = {
  open: 'crit',
  investigating: 'warn',
  resolved: 'ok',
  false_positive: 'muted'
};

const eventTypeIcons: Record<string, string> = {
  vulnerability: 'shield',
  access_anomaly: 'user',
  malware: 'alert-triangle',
  policy_violation: 'settings',
  network_intrusion: 'activity'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function SecurityEvents() {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    // Simulate API call
    const loadEvents = async () => {
      setLoading(true);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setEvents(mockEvents);
      setLoading(false);
    };
    
    loadEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesFilter = filter === 'all' || event.severity === filter || event.status === filter;
    const matchesSearch = search === '' || 
      event.title.toLowerCase().includes(search.toLowerCase()) ||
      event.description.toLowerCase().includes(search.toLowerCase()) ||
      event.source.toLowerCase().includes(search.toLowerCase()) ||
      event.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const eventStats = {
    total: events.length,
    critical: events.filter(e => e.severity === 'critical').length,
    open: events.filter(e => e.status === 'open').length,
    investigating: events.filter(e => e.status === 'investigating').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading security events...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Security Events"
        subtitle="Monitor and analyze security events from your integrated sources in real-time."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="plus" size={14} stroke="white" /> Create Alert Rule
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Events</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{eventStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Critical</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{eventStats.critical}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Open</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#CA9401' }}>{eventStats.open}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Investigating</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#0A69DA' }}>{eventStats.investigating}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="all">All Events</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="open">Open</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Security Events ({filteredEvents.length})
        </div>
        
        {filteredEvents.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mb-4 text-ink/40">
              <Icon name="search" size={48} />
            </div>
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">No events found</h3>
            <p className="font-display text-[14px] text-ink/60">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <ul>
            {filteredEvents.map((event, index) => (
              <li 
                key={event.id} 
                className={cx(
                  'px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0',
                  'grid gap-4 items-start',
                  'grid-cols-[auto_1fr_auto_auto_auto]'
                )}
              >
                {/* Icon & Type */}
                <div className="flex items-center gap-3 pt-1">
                  <div className="h-8 w-8 rounded-[6px] bg-[#F7EDDB] grid place-items-center">
                    <Icon name={eventTypeIcons[event.type] as any} size={16} stroke="#CA9401" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-ink text-[13px] capitalize">
                      {event.type.replace('_', ' ')}
                    </div>
                    <div className="font-data text-[11px] text-ink/50">{event.source}</div>
                  </div>
                </div>

                {/* Title & Description */}
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-ink text-[14px] mb-1 line-clamp-1">
                    {event.title}
                  </h3>
                  <p className="font-display text-[13px] text-ink/65 line-clamp-2 mb-2">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {event.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#EDE5FC] text-[#7F56D9] font-display text-[10px] font-semibold">
                        {tag}
                      </span>
                    ))}
                    {event.tags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#EEEFF2] text-[#6B7280] font-display text-[10px] font-semibold">
                        +{event.tags.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Severity */}
                <Badge tone={severityTone[event.severity]} dot className="capitalize">
                  {event.severity}
                </Badge>

                {/* Status */}
                <Badge tone={statusTone[event.status]} className="capitalize">
                  {event.status.replace('_', ' ')}
                </Badge>

                {/* Time */}
                <div className="text-right">
                  <div className="font-display text-[12px] text-ink/60">
                    {formatTimeAgo(event.timestamp)}
                  </div>
                  {event.assignee && (
                    <div className="font-data text-[10px] text-ink/40 mt-1">
                      @{event.assignee}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}